import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  X, Pin, PinOff, ExternalLink, Star, CheckCircle2, AlertTriangle,
  Languages, Wallet, Clock, Loader2, ListChecks, ChevronRight,
} from 'lucide-react'
import { cn } from '../../../lib/utils'

// Full-screen sheet showing every detail of one country.
// Header: flag + name + scores + pin toggle.
// Body sections: visa paths, dual citizenship, languages, cost, pros, cons, official links, notes.

const STATUSES = ['researching', 'interested', 'pursuing', 'not_interested']

export default function CountryDetailSheet({ open, country, userState, onClose, onUpdate }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isAr = i18n.language === 'ar'
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && userState) setNotes(userState.notes || '')
    else if (open) setNotes('')
  }, [open, userState?.notes])

  useEffect(() => {
    const prev = document.body.style.overflow
    if (open) document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (!open || !country) return null

  const name = isAr ? country.name_ar : country.name_en
  const prLabel = isAr ? country.pr_label_ar : country.pr_label_en
  const pros = isAr ? country.pros_ar : country.pros_en
  const cons = isAr ? country.cons_ar : country.cons_en
  const dualNotes = isAr ? country.dual_citizenship_notes_ar : country.dual_citizenship_notes_en

  const status = userState?.status || 'researching'
  const pinned = userState?.pinned || false

  const togglePin = async () => {
    setSaving(true)
    await onUpdate({ country_slug: country.slug, pinned: !pinned })
    setSaving(false)
  }
  const changeStatus = async (newStatus) => {
    setSaving(true)
    await onUpdate({ country_slug: country.slug, status: newStatus })
    setSaving(false)
  }
  const saveNotes = async () => {
    setSaving(true)
    await onUpdate({ country_slug: country.slug, notes })
    setSaving(false)
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex flex-col"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-background mt-auto rounded-t-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-input flex items-center gap-3 flex-shrink-0">
          <span className="text-3xl">{country.flag_emoji}</span>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-foreground" dir="auto">{name}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <ScoreBadge label={t('immigration.tolerance')} score={country.tolerance_score} />
              <ScoreBadge label={t('immigration.stability')} score={country.stability_score} />
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 font-mono">
                {country.citizenship_years} {t('immigration.years')}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={togglePin}
            disabled={saving}
            aria-label={pinned ? t('immigration.unpin') : t('immigration.pin')}
            className={cn(
              'p-2 rounded-md transition-colors',
              pinned ? 'bg-app-primary-500 text-white' : 'text-muted-foreground hover:bg-accent',
            )}
          >
            {pinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-2 rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Open Tracker — only meaningful once status is past
              researching. Always-on actually, because the tracker
              works fine for any country the user wants to plan. */}
          <button
            type="button"
            onClick={() => {
              onClose()
              navigate(`/immigration/${country.slug}`)
            }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-app-primary-500/10 text-app-primary-500 text-sm font-semibold hover:bg-app-primary-500/15"
            dir="auto"
          >
            <ListChecks className="w-4 h-4" />
            <span className="flex-1 text-start">{t('immigration.openTracker')}</span>
            <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
          </button>

          {/* Status chips */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2" dir="auto">
              {t('immigration.statusLabel')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => changeStatus(s)}
                  disabled={saving}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    status === s
                      ? 'bg-app-primary-500 text-white border-app-primary-500'
                      : 'border-input text-muted-foreground hover:bg-accent',
                  )}
                >
                  {t(`immigration.status.${s}`)}
                </button>
              ))}
            </div>
          </section>

          {/* Quick facts */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2" dir="auto">
              {t('immigration.quickFacts')}
            </h3>
            <div className="rounded-lg border border-input divide-y divide-input">
              <Fact icon={Clock} label={t('immigration.citizenshipPath')} value={prLabel} />
              <Fact icon={Languages} label={t('immigration.languages')} value={(country.primary_languages || []).join(' · ')} />
              <Fact
                icon={Wallet}
                label={t('immigration.monthlyCost')}
                value={`$${country.monthly_living_estimate_usd_min}-${country.monthly_living_estimate_usd_max} / mo`}
              />
              <Fact
                icon={country.dual_citizenship_allowed ? CheckCircle2 : AlertTriangle}
                iconClass={country.dual_citizenship_allowed ? 'text-emerald-600' : 'text-amber-600'}
                label={t('immigration.dualCitizenship')}
                value={dualNotes}
              />
            </div>
          </section>

          {/* Visa paths */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2" dir="auto">
              {t('immigration.visaPaths')}
            </h3>
            <div className="space-y-2">
              {(country.main_visa_paths || []).map((path) => (
                <div key={path.key} className="rounded-lg border border-input p-3" dir="auto">
                  <p className="text-sm font-semibold text-foreground">
                    {isAr ? path.title_ar : path.title_en}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {isAr ? path.summary_ar : path.summary_en}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Pros / Cons */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3" dir="auto">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1.5">
                {t('immigration.pros')}
              </p>
              <ul className="space-y-1 list-disc list-inside text-xs">
                {(pros || []).map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3" dir="auto">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1.5">
                {t('immigration.cons')}
              </p>
              <ul className="space-y-1 list-disc list-inside text-xs">
                {(cons || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          </section>

          {/* Official links */}
          {(country.official_links || []).length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2" dir="auto">
                {t('immigration.officialLinks')}
              </h3>
              <div className="space-y-1.5">
                {country.official_links.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-app-primary-500 hover:underline" dir="auto"
                  >
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{isAr ? link.label_ar : link.label_en}</span>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Notes */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2" dir="auto">
              {t('immigration.notes')}
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder={t('immigration.notesPlaceholder')}
              rows={4}
              className="w-full text-sm rounded-md border border-input bg-background p-3 text-foreground"
            />
            {saving && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> {t('common.saving')}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function ScoreBadge({ label, score }) {
  return (
    <span className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-muted text-muted-foreground" title={label}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn('w-2.5 h-2.5', i < score ? 'fill-app-primary-500 text-app-primary-500' : 'text-muted-foreground/30')}
        />
      ))}
    </span>
  )
}

function Fact({ icon: Icon, iconClass, label, value }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3 p-3" dir="auto">
      <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', iconClass || 'text-muted-foreground')} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-sm text-foreground mt-0.5 leading-relaxed">{value}</p>
      </div>
    </div>
  )
}
